// Пречките пред записа на доставка — чиста функция и ЕДНА структура (поле ·
// продукти · текст · клетка за фокус), от която идват и трите: текстът в
// съобщението и футъра, червената рамка на клетката и къде отива курсорът при
// „Запиши" / Ctrl+Enter (на таблицата и на картите — едни и същи id-та).
//
// Редът е редът на ЕКРАНА: редовете в реда на добавяне (`addedIds`, не
// `Object.entries` — числовите ключове там са по id), а в реда — колоните отляво
// надясно. Пречките се групират по поле в реда на първата си клетка, затова
// първата пречка сочи първата проблемна клетка на екрана.
//
// Празна цена (`""`) е пречка; изрично въведената 0 НЕ е (подарена стока), а
// цената, изведена от въведено Нето/Бруто, вече е попълнена.
import type { TFunction } from "i18next";
import type { Product } from "../../../../api/inventory";
import { isRowTouched, parseDecimal, type DeliveryRowDraft } from "./deliveryWorksheetShared";

export type DeliveryIssueField = "qty" | "price" | "batch" | "expiry" | "warehouse";

// Колоната на полето в решетката (`cellId`): 1 кол. · 2 цена · 5 партида · 6 срок · 7 склад
export const DELIVERY_FIELD_COLUMN: Record<DeliveryIssueField, number> = {
  qty: 1,
  price: 2,
  batch: 5,
  expiry: 6,
  warehouse: 7,
};
const FIELDS_BY_COLUMN: DeliveryIssueField[] = ["qty", "price", "batch", "expiry", "warehouse"];

// Колко имена изброява пречката, преди „и още N"
const LISTED_NAMES = 3;

export type DeliveryIssue = {
  // поле на редовете; „header" — пречка на документа (склад, доставчик);
  // „empty" — нищо за записване
  kind: DeliveryIssueField | "header" | "empty";
  text: string;
  // редовете с това поле празно, в реда на екрана (за червената рамка)
  productIds: number[];
  // първата му клетка — фокусът при „Запиши"
  focus?: { productId: number; col: number };
};

const ISSUE_TEXT_KEY: Record<DeliveryIssueField, string> = {
  qty: "inventoryPage.issueQty",
  price: "inventoryPage.issuePrice",
  batch: "inventoryPage.issueBatchFor",
  expiry: "inventoryPage.issueExpiryFor",
  warehouse: "inventoryPage.issueWarehouse",
};

const missingFields = (row: DeliveryRowDraft, product: Product | undefined): DeliveryIssueField[] => {
  const missing: Record<DeliveryIssueField, boolean> = {
    qty: parseDecimal(row.qty) <= 0,
    price: row.price === "",
    batch: !!product?.trackBatch && !row.batch.trim(),
    expiry: !!product?.trackExpiry && !row.expiry,
    warehouse: row.warehouseId == null,
  };
  return FIELDS_BY_COLUMN.filter((field) => missing[field]);
};

type Params = {
  warehouseId: number | null;
  hasWarehouses: boolean;
  // редовете в реда на екрана
  entries: [number, DeliveryRowDraft][];
  productById: Map<number, Product>;
  // има поне един ред за записване
  hasValidEntries: boolean;
  t: TFunction;
};

export function buildDeliveryIssues({
  warehouseId,
  hasWarehouses,
  entries,
  productById,
  hasValidEntries,
  t,
}: Params): DeliveryIssue[] {
  // Без НИТО ЕДИН склад „Изберете склад" подсказва избор, който го няма.
  if (!warehouseId)
    return [
      {
        kind: "header",
        text: t(hasWarehouses ? "inventoryPage.errWarehouse" : "inventoryPage.errNoWarehouses"),
        productIds: [],
      },
    ];

  const byField = new Map<DeliveryIssueField, number[]>();
  for (const [productId, row] of entries) {
    if (!isRowTouched(row)) continue;
    for (const field of missingFields(row, productById.get(productId))) {
      byField.set(field, [...(byField.get(field) ?? []), productId]);
    }
  }

  const nameOf = (productId: number) => productById.get(productId)?.name ?? `#${productId}`;
  const issues: DeliveryIssue[] = [...byField.entries()].map(([field, productIds]) => {
    const names = productIds.slice(0, LISTED_NAMES).map(nameOf).join(", ");
    const more = productIds.length - LISTED_NAMES;
    return {
      kind: field,
      text: t(ISSUE_TEXT_KEY[field], {
        products: more > 0 ? `${names} ${t("inventoryPage.stkIssueMore", { count: more })}` : names,
      }),
      productIds,
      focus: { productId: productIds[0], col: DELIVERY_FIELD_COLUMN[field] },
    };
  });
  if (!issues.length && !hasValidEntries)
    issues.push({ kind: "empty", text: t("inventoryPage.issueEmpty"), productIds: [] });
  return issues;
}

// Червената рамка: клетката на поле, което е пречка за този ред
export const invalidCellKeys = (issues: DeliveryIssue[]) =>
  new Set(issues.flatMap((issue) => issue.productIds.map((productId) => `${productId}:${issue.kind}`)));
